import React, { useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { string } from 'prop-types'
import axios from '@fs/zion-axios'
import {
  HeaderBlock,
  DialogOverlay,
  DialogOverlayContent,
  InputRow,
  useOverlay,
  Grid,
  Cell,
  Card,
  ZionForm,
  TextField,
  useAtSize,
  useStatusOverlay,
  Spinner,
  Button,
  TextArea,
} from '@fs/zion-ui'
import { trackEvent } from '@fs/zion-analytics'
import { ContentAdd } from '@fs/zion-icon'
import { useUser } from '@fs/zion-user'
import filterMemories from '../../../../../helpers/memory_helper'
import { usePostStoriesMemories, useDeleteMemory } from '../../../hooks/postHooks'
import useMemories from '../../../../../hooks/useMemories'
import DSRenderWithMode from '../../../../../helpers/DSRenderWithMode'
import DeleteMemoryButton from '../../../../DeleteMemoryButton/DeleteMemoryButton'
import { usePersonViewerState } from '../../../state/PersonViewerStore'

const cssAddMemory = css`
  border: 4px dotted #e0e0e0;
  margin: 10px 20px;
  cursor: pointer;
  font-size: 14px;
  color: #999;
  vertical-align: top;
  width: 200px;
  height: 200px;
  align-self: center;
  background-color: #fff;
`

const storyTitleCardCSS = css`
  font-weight: bold;
  padding: 10px;
`

const storyGridContainerCSS = css`
  margin: 20px;
`

const spinnerContainer = css`
  margin: auto 0;
`

export default function PersonStories({ personId, gender, centerOrStation }) {
  const user = useUser()
  const memoryType = 'story'
  const [isLoading, setIsLoading] = useState(true)
  const [currentStories, setCurrentStories] = useState([])
  const [currentStoryTitle, setCurrentStoryTitle] = useState('')
  const [currentStoryText, setCurrentStoryText] = useState('')
  const [t] = useTranslation()
  const atSize = useAtSize()
  const [newStory, postNewStory] = usePostStoriesMemories()
  const [memoriesState, dispatch] = useMemories({ personId })
  const [oldMemory, deleteMemory] = useDeleteMemory()
  const storyoverlay = useOverlay()
  const inputStoryOverlay = useOverlay()
  const showStatusOverlay = useStatusOverlay()
  const personViewerState = usePersonViewerState()
  const { lockMemories } = personViewerState

  const banner = css`
    width: 80%;
    background-color: #fff;
    text-align: center;
    margin: 0 auto;
    padding-bottom: 5px;
    margin-top: ${atSize({ default: '75px', xxl: '200px' })};
  `

  const bannerBtn = css`
    display: 'flex';
    flex-direction: 'column';
    align-items: 'center';
    margin-top: 20px;
  `

  React.useEffect(() => {
    if (memoriesState.complete && memoriesState.res && memoriesState.res.sourceDescriptions) {
      const images = filterMemories({ memories: memoriesState.res, memoryType: 'text/plain' })
      setCurrentStories(images)
      setIsLoading(false)
    } else if (memoriesState.complete && memoriesState.res === '') {
      setIsLoading(false)
    }
  }, [memoriesState])

  React.useEffect(() => {
    if (newStory.pending) {
      showStatusOverlay({
        message: t(`person-details.uploading-memory-${memoryType}`),
        type: 'normal',
        dismissable: true,
      })
    }
  }, [newStory.pending, showStatusOverlay, t])

  React.useEffect(() => {
    if (newStory.complete && !newStory.error && !newStory.pending) {
      dispatch({ type: 'load' })
      showStatusOverlay({
        message: t(`person-details.upload-successful-${memoryType}`),
        type: 'success',
        dismissable: true,
      })
    }
  }, [dispatch, newStory, showStatusOverlay, t])

  React.useEffect(() => {
    if (newStory.complete && newStory.error && !newStory.pending) {
      showStatusOverlay({
        message: t(
          `person-details.status-${newStory.error?.response?.status || 'error'}${
            newStory.error?.response?.status === 409 ? `-${memoryType}` : ''
          }`
        ),
        type: 'error',
        dismissable: true,
      })
    }
  }, [newStory, showStatusOverlay, t])

  React.useEffect(() => {
    if (oldMemory.complete && !oldMemory.error && !oldMemory.pending) {
      dispatch({ type: 'load' })
    }
  }, [dispatch, oldMemory])

  const openStoryDialog = () => {
    inputStoryOverlay.open()
  }

  const uploadStory = (values, formikBag) => {
    const fd = new FormData()
    values.story = new File([values.storyText], `${values.title}.txt`, { type: 'text/plain' })
    Object.keys(values).forEach((value) => {
      fd.append(value, values[value])
    })

    postNewStory({ data: fd })
    trackEvent({ event_name: 'Upload Story' })
    inputStoryOverlay.close()
    formikBag.resetForm()
  }

  const clearStoryForm = () => {
    inputStoryOverlay.close()
  }

  const openDialog = (title, link) => {
    axios.get(link).then(({ data }) => {
      setCurrentStoryTitle(title)
      const formattedData = data.replace(/_____/gim, '-----')
      setCurrentStoryText(formattedData)
      storyoverlay.open()
    })
  }

  const validate = (values) => {
    const errors = {}

    if (!values.storyText || (values.storyText && values.storyText.length === 0)) {
      errors.storyText = t('person-details.story-text-required', 'A Story Text is Required.')
    }

    if (!values.title || (values.title && values.title.length === 0)) {
      errors.title = t('person-details.story-title-required', 'A Story Title is Required.')
    }

    if (values.title && values.title.length > 30) {
      errors.title = t('person-details.title-less-than', 'Title should be no more than 30 characters')
    }

    return errors
  }

  return (
    <>
      {!isLoading && currentStories.length > 0 && (
        <div>
          <div css={storyGridContainerCSS}>
            <Grid>
              {!lockMemories && !centerOrStation && (
                <DSRenderWithMode allowedModes={['WEB']}>
                  <Cell columns={atSize({ default: 12, sm: 6, lg: 4, xl: 3, xxl: 2 })} key="add-button-cell">
                    <button css={cssAddMemory} type="button" onClick={openStoryDialog}>
                      <ContentAdd size="xl" color="#aaa" />
                      <div>{t('person-details.write-story', 'Write a story')}</div>
                    </button>
                  </Cell>
                </DSRenderWithMode>
              )}
              {currentStories.map((story) => (
                <Cell columns={atSize({ default: 12, sm: 6, lg: 4, xl: 3, xxl: 2 })} key={story.link}>
                  <Card
                    onClick={() => openDialog(story.title, story.link)}
                    role="button"
                    tabIndex={-1}
                    onKeyPress={() => openDialog(story.title, story.link)}
                  >
                    <Card.Body>
                      <div css={storyTitleCardCSS}>{story.title}</div>
                      <div css={{ padding: '10px' }}>{story.description}</div>
                    </Card.Body>
                    {story.resourceId === user.cisId && !lockMemories && !centerOrStation && (
                      <Card.Actions>
                        <DeleteMemoryButton artifactId={story.id} deleteMemory={deleteMemory} />
                      </Card.Actions>
                    )}
                  </Card>
                </Cell>
              ))}
              {(newStory.pending || oldMemory.pending) && (
                <Cell columns={atSize({ default: 12, sm: 6, lg: 4, xl: 3, xxl: 2 })}>
                  <div css={spinnerContainer}>
                    <Spinner large />
                  </div>
                </Cell>
              )}
            </Grid>
          </div>
        </div>
      )}
      {!isLoading && currentStories.length === 0 && (
        <div css={banner}>
          {lockMemories && <HeaderBlock size="md" heading={t('person-details.no-records', 'No records found')} />}
          {centerOrStation && (
            <HeaderBlock
              size="md"
              heading={t('person-details.be-first-center', 'Be the first to add to this record on FamilySearch.org')}
            />
          )}
          {!lockMemories && !centerOrStation && (
            <div>
              <HeaderBlock size="md" heading={t('person-details.be-first', 'Be the first to add to this record')} />
              <div css={bannerBtn}>
                <Button
                  emphasis="billboard"
                  color={gender && gender === 'male' ? 'blue' : 'red'}
                  onClick={openStoryDialog}
                >
                  {t('person-details.write-story', 'Write a story')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      {isLoading && currentStories.length === 0 && (
        <div css={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '500px' }}>
          <Spinner large />
        </div>
      )}

      <DialogOverlay headingText={currentStoryTitle} {...storyoverlay}>
        <DialogOverlayContent>
          <div>{currentStoryText}</div>
        </DialogOverlayContent>
      </DialogOverlay>

      <DialogOverlay headingText={t('person-details.write-story', 'Write a story')} {...inputStoryOverlay}>
        <ZionForm
          validate={validate}
          onSubmit={uploadStory}
          onReset={clearStoryForm}
          initialValues={{ ancestorId: personId }}
        >
          {({ isValid, handleReset, submitForm, ...formProps }) => (
            <DialogOverlayContent>
              <TextField
                name="title"
                label={t('common-ui:title.item', 'Title')}
                helpMessage={t('person-details.title-less-than', 'Title should be no more than 30 characters')}
                value={formProps.values.title}
                placeholder={t('person-details.enter-title', 'Enter a title')}
                maxLength={150}
              />
              <TextArea
                name="storyText"
                label={t('person-details.story', 'Story')}
                helpMessage={t('person-details.story-text-required', 'A Story Text is Required.')}
                placeholder={t('person-details.enter-story', 'Enter a story here')}
                value={formProps.values.storyText}
              />
              <InputRow>
                <Button onClick={handleReset}>{t('common-ui:cancel.action', 'Cancel')}</Button>
                <Button
                  disabled={!isValid || formProps.isSubmitting}
                  type="submit"
                  onClick={submitForm}
                  emphasis="high"
                >
                  {t('common-ui:save.action', 'Save')}
                </Button>
              </InputRow>
            </DialogOverlayContent>
          )}
        </ZionForm>
      </DialogOverlay>
    </>
  )
}

PersonStories.propTypes = {
  personId: string,
}
